import { useContext, useState } from 'react';
import { ContentContext } from '../../contexts/ContentContext';
import useContent from '../../hooks/useContent';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';
import AdminTopbar from '../AdminTopbar';
import SaveBar from '../components/SaveBar';
import ConfirmModal from '../components/ConfirmModal';

export default function BenefitsEditor() {
  const { content, updateSection } = useContext(ContentContext);
  const { draft, isDirty, saving, lastSaved, update, save, discard } =
    useContent(content.benefits);
  useUnsavedChanges(isDirty);

  const [deleteIdx, setDeleteIdx] = useState(null);

  const handleSave    = () => save(patch => updateSection('benefits', patch));
  const handleDiscard = () => discard(content.benefits);

  const items = draft.items || [];

  const updateItem = (i, patch) =>
    update({ items: items.map((c, idx) => idx === i ? { ...c, ...patch } : c) });

  const addItem = () =>
    update({ items: [...items, { title: 'New Benefit', desc: '' }] });

  const removeItem = (i) =>
    update({ items: items.filter((_, idx) => idx !== i) });

  const moveItem = (from, to) => {
    const next = [...items];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    update({ items: next });
  };

  return (
    <div className="adm-page">
      <AdminTopbar title="Benefits" />
      <SaveBar isDirty={isDirty} saving={saving} lastSaved={lastSaved}
               onSave={handleSave} onDiscard={handleDiscard} />

      {deleteIdx !== null && (
        <ConfirmModal
          message={`Delete benefit "${items[deleteIdx]?.title}"?`}
          onConfirm={() => { removeItem(deleteIdx); setDeleteIdx(null); }}
          onCancel={() => setDeleteIdx(null)}
        />
      )}

      <div className="adm-editor">
        <section className="adm-section">
          <h3 className="adm-section__title">Section Heading</h3>

          <div className="adm-field">
            <label className="adm-label">Heading</label>
            <input className="adm-input" value={draft.heading || ''}
              onChange={e => update({ heading: e.target.value })} />
          </div>
        </section>

        <section className="adm-section">
          <h3 className="adm-section__title">Benefit Cards</h3>

          <div className="adm-card-list">
            {items.map((item, i) => (
              <div key={i} className="adm-card-item" draggable
                onDragStart={e => e.dataTransfer.setData('text/plain', i)}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); moveItem(+e.dataTransfer.getData('text/plain'), i); }}>

                <div className="adm-card-item__header">
                  <span className="adm-list__drag">⠿</span>
                  <span className="adm-label" style={{ flex: 1 }}>Card {i + 1}</span>
                  <button className="adm-btn adm-btn--danger adm-btn--sm"
                    onClick={() => setDeleteIdx(i)}>✕</button>
                </div>

                <div className="adm-field">
                  <label className="adm-label">Title</label>
                  <input className="adm-input" value={item.title || ''}
                    onChange={e => updateItem(i, { title: e.target.value })} />
                </div>

                <div className="adm-field">
                  <label className="adm-label">Description</label>
                  <textarea className="adm-textarea" rows={2} value={item.desc || ''}
                    onChange={e => updateItem(i, { desc: e.target.value })} />
                </div>
              </div>
            ))}
          </div>

          <button className="adm-btn adm-btn--ghost" onClick={addItem}>+ Add Card</button>
        </section>
      </div>
    </div>
  );
}
